import React, { useState, useEffect } from 'react';
import { fetchSettings, saveSettings } from '../services/db';

const PRESET_COLORS = [
  { label: 'Indigo', value: '#6366f1' },
  { label: 'Violet', value: '#8b5cf6' },
  { label: 'Rose', value: '#f43f5e' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Emerald', value: '#10b981' },
  { label: 'Sky', value: '#0ea5e9' },
  { label: 'Schwarz', value: '#111827' },
];

export default function SettingsPage({ user, onBack, planTier }) {
  const [brandColor, setBrandColor] = useState('#6366f1');
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchSettings(user.uid).then(s => {
      if (s.brandColor) setBrandColor(s.brandColor);
    });
  }, [user.uid]);

  const handleSaveColor = async () => {
    setSaving(true);
    await saveSettings(user.uid, { brandColor });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleCancelSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await fetch('/.netlify/functions/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error(e);
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-10">
      <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 mb-8 transition-colors">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10 6H2M5 3L2 6L5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
        Zurück
      </button>

      <h1 className="text-xl font-semibold text-gray-900 mb-8">Einstellungen</h1>

      {/* Brand Color */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-4">
        <p className="text-sm font-semibold text-gray-800 mb-1">Kunden-Seite anpassen</p>
        <p className="text-xs text-gray-400 mb-5">Die Akzentfarbe erscheint auf der Seite, die du an deine Kunden schickst.</p>

        {/* Preview */}
        <div className="rounded-xl border border-gray-100 overflow-hidden mb-5">
          <div className="h-10 flex items-center px-4 gap-2 border-b border-gray-100" style={{ backgroundColor: brandColor + '15' }}>
            <div className="w-5 h-5 rounded flex items-center justify-center" style={{ backgroundColor: brandColor }}>
              <svg width="9" height="9" viewBox="0 0 14 14" fill="none"><path d="M2 1.5L12 7L2 12.5V1.5Z" fill="white"/></svg>
            </div>
            <span className="text-xs font-semibold text-gray-700">FrameIt</span>
            <span className="ml-auto text-xs text-gray-400">Kunden-Ansicht</span>
          </div>
          <div className="px-4 py-3 bg-gray-50 flex items-center gap-3">
            <div className="flex-1">
              <div className="h-2.5 rounded bg-gray-200 w-3/4 mb-1.5" />
              <div className="h-2 rounded bg-gray-100 w-1/2" />
            </div>
            <div className="rounded-lg px-3 py-1.5 text-xs font-medium text-white" style={{ backgroundColor: brandColor }}>
              Senden
            </div>
          </div>
        </div>

        {/* Presets */}
        <div className="flex flex-wrap gap-2 mb-4">
          {PRESET_COLORS.map(c => (
            <button
              key={c.value}
              onClick={() => setBrandColor(c.value)}
              title={c.label}
              className={`w-8 h-8 rounded-full border-2 transition-all ${brandColor === c.value ? 'border-gray-700 scale-110' : 'border-transparent hover:scale-105'}`}
              style={{ backgroundColor: c.value }}
            />
          ))}
          <input
            type="color"
            value={brandColor}
            onChange={e => setBrandColor(e.target.value)}
            className="w-8 h-8 rounded-full border-2 border-dashed border-gray-300 cursor-pointer overflow-hidden p-0"
            title="Eigene Farbe"
          />
        </div>

        <button
          onClick={handleSaveColor}
          disabled={saving}
          className="px-4 py-2 text-sm font-semibold text-white rounded-xl transition-all disabled:opacity-50"
          style={{ backgroundColor: brandColor }}
        >
          {saving ? 'Speichern…' : saved ? '✓ Gespeichert' : 'Farbe speichern'}
        </button>
      </div>

      {/* Subscription */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-semibold text-gray-800">Abo verwalten</p>
          {planTier === 'agency' ? (
            <span className="text-xs bg-purple-100 text-purple-700 font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 1l1.5 3H11l-2.5 2 1 3L6 7.5 2.5 9l1-3L1 4h3.5L6 1z" fill="currentColor"/></svg>
              Agency Plan
            </span>
          ) : (
            <span className="text-xs bg-indigo-50 text-indigo-600 font-semibold px-2.5 py-1 rounded-full">Standard Plan</span>
          )}
        </div>
        <p className="text-xs text-gray-400 mb-5">Im Stripe-Kundenportal kannst du dein Abo kündigen, pausieren oder die Zahlungsmethode ändern.</p>
        <button
          onClick={handleCancelSubscription}
          disabled={portalLoading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-xl hover:bg-red-50 disabled:opacity-50 transition-all"
        >
          {portalLoading && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
          Abo verwalten / kündigen
        </button>
      </div>
    </div>
  );
}
